//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { Fragment } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import FacebookIcon from '@material-ui/icons/Facebook';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import YouTubeIcon from '@material-ui/icons/YouTube';
import GitHubIcon from '@material-ui/icons/GitHub';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getCityStateCountryText } from '../../common/textHelper';
import TiktokIcon from '../../assets/tiktok_icon.png';
import XIcon from '../../assets/x_logo_black.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { getCompanySizeText } from '../../common/textHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
// import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import AfitdIconButton from '../../common/elemental/AfitdIconButton';
import AfitdCompanyName from '../../common/elemental/AfitdCompanyName';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const {
        industry,
        companySize,
        foundedYr,
        // facebookUrl,
        // linkedInUrl,
        // instagramUrl,
        // twitterUrl,
        // youTubeUrl,
        websiteUrl,
    } = params;

    return makeStyles(theme => ({
        root: {
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '10px',
            marginRight: '24px',
            padding: '40px',
            [theme.breakpoints.down('lg')]: { padding: 32 },
            [theme.breakpoints.down('md')]: { padding: 16 },
            [theme.breakpoints.down('sm')]: { marginRight: '0px' },
        },
        industryContainer: { display: industry ? 'block' : 'none' },
        companySizeContainer: { display: companySize ? 'block' : 'none' },
        foundedYrContainer: { display: foundedYr ? 'block' : 'none' },
        websiteContainer: { display: websiteUrl ? 'block' : 'none' },
        label: {
            fontSize: '12px',
            lineHeight: '24px',
            margin: '0px',
        },
        socialLabel: {
            fontSize: '12px',
            lineHeight: '24px',
            marginTop: '0px',
            borderTop: '2px solid #F0F2F6',
            paddingTop: '16px',
        },
        propsText: {
            fontSize: '16px',
            fontWeight: 'bold',
            lineHeight: '28px',
            color: 'black',
            marginTop: '0px',
        },
        industryIcon: {
            position: 'relative',
            top: '3.5px',
        },
        socialMediaButtonsContainer: {},
        facebookIconButton: { color: 'black', display: 'inline' },
        linkedInIconButton: { color: 'black', display: 'inline' },
        instagramIconButton: { color: 'black', display: 'inline' },
        twitterIconButton: { color: 'black', display: 'inline' },
        youTubeIconButton: { color: 'black', display: 'inline' },
        tiktokIconButton: { color: 'black', display: 'inline' },
        gitHubIconButton: { color: 'black', display: 'inline' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        // props,
        classes,
        companyName,
        isEmpBadge,
        facebookUrl,
        linkedInUrl,
        instagramUrl,
        twitterUrl,
        youTubeUrl,
        tikTokUrl,
        gitHubUrl,
        // websiteUrl,
        handleClickSocialButton,
    } = params;

    const defns = {
        companyName: { companyName, isEmpBadge },
        facebookIconButton: { onClick: handleClickSocialButton.bind(null, facebookUrl), isHidden: !facebookUrl },
        linkedInIconButton: { onClick: handleClickSocialButton.bind(null, linkedInUrl), isHidden: !linkedInUrl },
        instagramIconButton: { onClick: handleClickSocialButton.bind(null, instagramUrl), isHidden: !instagramUrl },
        twitterIconButton: { onClick: handleClickSocialButton.bind(null, twitterUrl), isHidden: !twitterUrl },
        youTubeIconButton: { onClick: handleClickSocialButton.bind(null, youTubeUrl), isHidden: !youTubeUrl },
        tiktokIconButton: { onClick: handleClickSocialButton.bind(null, tikTokUrl), isHidden: !tikTokUrl },
        gitHubIconButton: { onClick: handleClickSocialButton.bind(null, gitHubUrl), isHidden: !gitHubUrl },
        facebookIcon: { fontSize: 'large' },
        linkedInIcon: { fontSize: 'large' },
        instagramIcon: { fontSize: 'large' },
        twitterIcon: { fontSize: 'large' },
        youTubeIcon: { fontSize: 'large' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const SocialMediaSection = props => {
    const { defns } = props;

    const facebookIconButtonIsHidden = _get(defns, 'facebookIconButton.isHidden');
    const linkedInIconButtonIsHidden = _get(defns, 'linkedInIconButton.isHidden');
    const instagramIconButtonIsHidden = _get(defns, 'instagramIconButton.isHidden');
    const twitterIconButtonIsHidden = _get(defns, 'twitterIconButton.isHidden');
    const youTubeIconButtonIsHidden = _get(defns, 'youTubeIconButton.isHidden');
    const tiktokIconButtonIsHidden = _get(defns, 'tiktokIconButton.isHidden');
    const gitHubIconButtonIsHidden = _get(defns, 'gitHubIconButton.isHidden');

    const isHidden =
        facebookIconButtonIsHidden &&
        linkedInIconButtonIsHidden &&
        instagramIconButtonIsHidden &&
        twitterIconButtonIsHidden &&
        youTubeIconButtonIsHidden &&
        tiktokIconButtonIsHidden &&
        gitHubIconButtonIsHidden;

    return isHidden ? null : (
        <Fragment>
            <p {...defns.socialLabel}>Social Media:</p>
            <div {...defns.socialMediaButtonsContainer}>
                <AfitdIconButton {...defns.facebookIconButton}>
                    <FacebookIcon {...defns.facebookIcon} />
                </AfitdIconButton>
                <AfitdIconButton {...defns.linkedInIconButton}>
                    <LinkedInIcon {...defns.linkedInIcon} />
                </AfitdIconButton>
                <AfitdIconButton {...defns.instagramIconButton}>
                    <InstagramIcon {...defns.instagramIcon} />
                </AfitdIconButton>
                <AfitdIconButton {...defns.twitterIconButton}>
                    <img src={XIcon} alt="x_icon" width="20px" />
                </AfitdIconButton>
                <AfitdIconButton {...defns.youTubeIconButton}>
                    <YouTubeIcon {...defns.youTubeIcon} />
                </AfitdIconButton>
                <AfitdIconButton {...defns.tiktokIconButton}>
                    <img src={TiktokIcon} alt="tiktok_icon" width="30px" />
                </AfitdIconButton>
                <AfitdIconButton {...defns.gitHubIconButton}>
                    <GitHubIcon {...defns.gitHubIcon} />
                </AfitdIconButton>
            </div>
        </Fragment>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const CompanyInfo = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    // const layoutContext = useContext(LayoutContext);
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { companyInfoData, socialMediaData } = props;
    const companyName = _get(companyInfoData, 'companyName');
    const isEmpBadge = _get(companyInfoData, 'isEmpBadge');
    const industry = _get(companyInfoData, 'industry');
    const companySize = _get(companyInfoData, 'companySize');
    const companySizeText = getCompanySizeText(companySize, null);
    const foundedYr = _get(companyInfoData, 'foundedYr');
    const country = _get(companyInfoData, 'country');
    const city = _get(companyInfoData, 'city');
    const state = _get(companyInfoData, 'state');
    const facebookUrl = _get(socialMediaData, 'facebookUrl');
    const linkedInUrl = _get(socialMediaData, 'linkedInUrl');
    const instagramUrl = _get(socialMediaData, 'instagramUrl');
    const twitterUrl = _get(socialMediaData, 'twitterUrl');
    const youTubeUrl = _get(socialMediaData, 'youTubeUrl');
    const websiteUrl = _get(socialMediaData, 'websiteUrl');
    const tikTokUrl = _get(socialMediaData, 'tikTokUrl');
    const gitHubUrl = _get(socialMediaData, 'gitHubUrl');

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickSocialButton = url => {
        window.open(url);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ industry, companySize, foundedYr, facebookUrl, linkedInUrl, instagramUrl, twitterUrl, youTubeUrl, websiteUrl, tikTokUrl, gitHubUrl })();
    const defns = getDefns({
        props,
        classes,
        companyName,
        isEmpBadge,
        facebookUrl,
        linkedInUrl,
        instagramUrl,
        twitterUrl,
        youTubeUrl,
        websiteUrl,
        tikTokUrl,
        gitHubUrl,
        handleClickSocialButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <div>
                <p {...defns.label}>Company Name:</p>
                <AfitdCompanyName {...defns.companyName} />
            </div>
            <div {...defns.industryContainer}>
                <p {...defns.label}>Industry:</p>
                <p {...defns.propsText}>
                    <BusinessIcon {...defns.industryIcon} /> {industry}
                </p>
            </div>
            <div {...defns.foundedYrContainer}>
                <p {...defns.label}>Founded:</p>
                <p {...defns.propsText}>{`Founded in ${foundedYr}`}</p>
            </div>
            <div {...defns.companySizeContainer}>
                <p {...defns.label}>Company Size:</p>
                <p {...defns.propsText}>{companySizeText}</p>
            </div>
            <div>
                <p {...defns.label}>Headquarters/Head Office Location:</p>
                <p {...defns.propsText}>{getCityStateCountryText(city, state, country)} </p>
            </div>
            <div {...defns.websiteContainer}>
                <p {...defns.label}>Website:</p>
                <p {...defns.propsText}>
                    <a target="blank" href={websiteUrl}>
                        {websiteUrl}
                    </a>
                </p>
            </div>
            <SocialMediaSection defns={defns} />
        </div>
    );
};

export default CompanyInfo;
